import { FocusEvent, useCallback, useMemo, useState } from 'react';
import { useDebounce } from '../../useDebounce';
import { OptionsPage, OptionType } from '../../queryClient';
import { FieldPath, FieldValues } from 'react-hook-form';
import { InfiniteData } from '@tanstack/react-query';
import { useAutoClearFormFieldValueIfNotInOptions } from '../useAutoClearFormFieldValueIfNotInOptions';
import { FormSelect, FormSelectProps } from './FormSelect';

export interface FormInfiniteSearchSelectProps<
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
  Value extends FormData[Name] = FormData[Name],
  SearchOptions = any,
> extends FormSelectProps<FormData, Name, Value> {
  defaultOption?: OptionType;
  searchOptions?: SearchOptions;
}

/**
 * Add default option to first page if it does not exist or filter it out from
 * next pages.
 * @param content List of options of the page
 * @param page Page number
 * @param defaultOption Default option
 */
export const withDefaultOption = <T extends OptionType>(
  content: T[],
  page: number,
  defaultOption?: T | T[],
): T[] => {
  if (!defaultOption) {
    return content;
  }

  const defaultOptions = Array.isArray(defaultOption)
    ? defaultOption
    : [defaultOption];
  const defaultValues = new Set(defaultOptions.map((option) => option.value));

  if (page) {
    return content.filter((item) => !defaultValues.has(item.value));
  }

  const contentValues = new Set(content.map((item) => item.value));
  return content.concat(
    defaultOptions.filter((option) => !contentValues.has(option.value)),
  );
};

export const getPageOptions = <T extends OptionType>(
  pages: OptionsPage<T>[],
  defaultOption?: T,
): T[] => {
  const optionsByValue = new Map<T['value'], T>();

  pages.forEach((page, index) => {
    withDefaultOption(page.content, index, defaultOption).forEach((option) => {
      if (!optionsByValue.has(option.value)) {
        optionsByValue.set(option.value, option);
      }
    });
  });

  return Array.from(optionsByValue.values());
};

export const usePagedAutocomplete = () => {
  const [searched, setSearched] = useState('');
  const searchText = useDebounce(searched);

  const handleSearch = useCallback((term: string) => {
    setSearched(term);
  }, []);
  const handleReset = useCallback(() => {
    handleSearch('');
  }, [handleSearch]);

  return { handleReset, handleSearch, searchText };
};

export interface InfiniteSearchQueryResult<T extends OptionType> {
  data: InfiniteData<OptionsPage<T>> | undefined;
  fetchNextPage: () => PromiseLike<any>;
  hasNextPage?: boolean;
  isFetching: boolean;
}

export type InfiniteSearchHook<T extends OptionType, SearchOptions = any> = (
  searchText: string,
  searchOptions?: SearchOptions,
) => InfiniteSearchQueryResult<T>;

interface UseInfiniteSearchSelectOptions<
  T extends OptionType,
  SearchOptions = any,
> {
  useSearchHook: InfiniteSearchHook<T, SearchOptions>;
  searchOptions?: SearchOptions;
  onClear?: () => void;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onSearch?: (term: string) => void;
}

export const useInfiniteSearchSelect = <
  T extends OptionType,
  FetchOptions = any,
>({
  useSearchHook,
  searchOptions,
  onClear,
  onFocus,
  onSearch,
}: UseInfiniteSearchSelectOptions<T, FetchOptions>) => {
  const { handleReset, handleSearch, searchText } = usePagedAutocomplete();
  const { data, fetchNextPage, hasNextPage, isFetching } = useSearchHook(
    searchText,
    searchOptions,
  );

  return {
    data,
    selectProps: {
      allowClear: true,
      filterOption: false as const,
      loading: isFetching,
      showSearch: true,
      onClear: () => {
        handleReset();
        onClear?.();
      },
      onFocus: (event: FocusEvent<HTMLElement>) => {
        handleReset();
        onFocus?.(event);
      },
      onScrollDown: hasNextPage ? fetchNextPage : undefined,
      onSearch: (term: string) => {
        handleSearch(term);
        onSearch?.(term);
      },
    },
  };
};

export const createInfiniteSearchSelect =
  <
    FormData extends FieldValues,
    Name extends FieldPath<FormData> = FieldPath<FormData>,
    Value extends FormData[Name] = FormData[Name],
    SearchOptions = any,
  >(
    useSearchHook: InfiniteSearchHook<OptionType, SearchOptions>,
    autoClearFormFieldValueIfNotInOptions = false,
  ) =>
  (props: FormInfiniteSearchSelectProps<FormData, Name, Value>) => {
    const { name, searchOptions, onClear, onFocus, onSearch, ...rest } = props;
    const { data, selectProps } = useInfiniteSearchSelect({
      useSearchHook,
      searchOptions,
      onClear,
      onFocus,
      onSearch,
    });

    const allOptions = useMemo(
      () => (data ? data.pages.flatMap((page) => page.content) : []),
      [data],
    );

    useAutoClearFormFieldValueIfNotInOptions({
      name,
      options: allOptions,
      isEnabled: autoClearFormFieldValueIfNotInOptions && !selectProps.loading,
    });

    return (
      <FormSelect {...rest} {...selectProps} pages={data?.pages} name={name} />
    );
  };
